/**
 * HTTP-клиент с авто-инжектом заголовка `X-Telegram-Init-Data`.
 *
 * Все запросы идут на /api/* — Caddy реверс-проксирует их на FastAPI.
 * Используем относительные URL: благодаря этому Mini App работает как
 * на dev (https://inflow-dev.duckdns.org:8443/), так и в prod
 * (https://app.inflow.in.ua/) без перенастройки.
 */

import { getInitData } from "@/lib/telegram";

const API_BASE = "/api/v1";

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
    public maintenance: boolean = false,
    public code: string | null = null,
    /** Значення `Retry-After` з відповіді 429 (секунди), якщо сервер його дав. */
    public retryAfter: string | null = null,
  ) {
    super(`API ${status}: ${detail}`);
  }
}

// Прапор onboarding_required (403 з code=onboarding_required) — встановлюється
// в apiFetch (нижче) і там же скидається. Зовнішнього споживача нема: старий
// OnboardingGate видалено, редирект на /onboarding робить OnboardingRedirectGuard
// (App.tsx) через /me.onboarding_completed.
let _onboardingRequired = false;

export function setOnboardingRequired(active: boolean) {
  _onboardingRequired = active;
}

// Глобальний прапор maintenance — встановлюється при 503 + {maintenance:true}.
// UI може підписатися через addMaintenanceListener щоб показати банер.
let _maintenance = false;
const _listeners = new Set<(active: boolean, msg: string) => void>();
let _lastMsg = "";

export function isMaintenanceActive(): boolean {
  return _maintenance;
}

export function getMaintenanceMessage(): string {
  return _lastMsg;
}

export function addMaintenanceListener(
  fn: (active: boolean, msg: string) => void,
): () => void {
  _listeners.add(fn);
  // Одразу сповіщаємо про поточний стан
  fn(_maintenance, _lastMsg);
  return () => _listeners.delete(fn);
}

function setMaintenance(active: boolean, msg: string) {
  if (_maintenance === active && _lastMsg === msg) return;
  _maintenance = active;
  _lastMsg = msg;
  _listeners.forEach((fn) => fn(active, msg));
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  /** Свій таймаут для довгих запитів (AI-перевірка відгуку, розшифровка
   *  голосу). Дефолтних 15 с там мало: людина бачила «мережева помилка»,
   *  хоча сервер ще працював (owner 2026-08-12). */
  timeoutMs?: number;
}

// Жорсткий таймаут на КОЖЕН запит. Без нього завислий /me (мережа/сервер)
// тримав апку на вічному спінері — юзер думав «не грузиться» (owner 2026-07-08,
// @kutnaksu). Тепер запит падає за 15с → react-query показує помилку/ретрай,
// а не крутиться нескінченно.
const REQUEST_TIMEOUT_MS = 15000;

/**
 * Завантажити файл нашого API як blob і віддати object-URL.
 *
 * Потрібно там, де тег `<img>`/`<video>` не може поставити заголовок
 * `X-Telegram-Init-Data` — наприклад прикріплені до вакансії файли
 * (owner 2026-09-12). Хто викликає — той і робить `URL.revokeObjectURL`.
 */
export async function authedBlobUrl(path: string): Promise<string> {
  const initData = getInitData();
  if (!initData) {
    throw new ApiError(401, "Mini App opened outside Telegram (no initData)");
  }
  const url = path.startsWith("/api/v1") ? path : `${API_BASE}${path}`;
  const res = await fetch(url, {
    headers: { "X-Telegram-Init-Data": initData },
    cache: "no-store",
  });
  if (!res.ok) {
    throw new ApiError(res.status, `media ${res.status}`);
  }
  return URL.createObjectURL(await res.blob());
}

// 429 від сервера (owner 2026-10-11, захист від напливу): сервер просить
// зачекати й каже скільки — у заголовку `Retry-After` (секунди). Запити
// читання (GET) застосунок повторює сам, тихо: людина бачить той самий
// спінер на секунду довше, а не помилку. Запити, які щось ЗМІНЮЮТЬ (оплата,
// оберт колеса, збереження), не повторюємо ніколи — подвійна дія гірша за
// повідомлення «спробуй ще раз».
const RATE_LIMIT_RETRIES = 2;
const RATE_LIMIT_MAX_WAIT_MS = 5000;

/** Скільки чекати перед повтором: `Retry-After` у межах 1–5 с + розкид. */
export function rateLimitDelayMs(retryAfter: string | null, attempt: number): number {
  const sec = Number(retryAfter);
  const base = Number.isFinite(sec) && sec > 0 ? sec * 1000 : 1000 * (attempt + 1);
  const jitter = Math.floor(Math.random() * 400);
  return Math.min(RATE_LIMIT_MAX_WAIT_MS, Math.max(1000, base)) + jitter;
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? "GET";
  for (let attempt = 0; ; attempt++) {
    try {
      return await apiFetchOnce<T>(path, opts);
    } catch (e) {
      const limited = e instanceof ApiError && e.status === 429;
      if (!limited || method !== "GET" || attempt >= RATE_LIMIT_RETRIES) throw e;
      if (opts.signal?.aborted) throw e;
      await new Promise((r) =>
        setTimeout(r, rateLimitDelayMs((e as ApiError).retryAfter, attempt)),
      );
    }
  }
}

async function apiFetchOnce<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const initData = getInitData();
  if (!initData) {
    throw new ApiError(401, "Mini App opened outside Telegram (no initData)");
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(),
    opts.timeoutMs ?? REQUEST_TIMEOUT_MS,
  );
  if (opts.signal) {
    if (opts.signal.aborted) controller.abort();
    else opts.signal.addEventListener("abort", () => controller.abort());
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: opts.method ?? "GET",
      headers: {
        "Content-Type": "application/json",
        "X-Telegram-Init-Data": initData,
      },
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: controller.signal,
      // Telegram WebView має «липкий» fetch-кеш: GET-відповіді API (профіль,
      // підписка, баланс) могли віддаватись зі старого кешу → юзер зберіг
      // дату народження, а екран далі показував «не вказано» і знову
      // пропонував ввести (owner 2026-07-25). no-store вимикає це для ВСІХ
      // API-запитів.
      cache: "no-store",
    });
  } catch (e) {
    // AbortError (таймаут) або мережева помилка → ретрайабельна помилка,
    // а не вічне зависання.
    throw new ApiError(0, "Мережева помилка або таймаут. Спробуй ще раз.");
  } finally {
    clearTimeout(timeoutId);
  }

  if (!res.ok) {
    let detail = res.statusText;
    let isMaint = false;
    let code: string | null = null;
    try {
      const payload = (await res.json()) as {
        detail?: string | { code?: string; message?: string };
        maintenance?: boolean;
      };
      if (typeof payload.detail === "string") {
        detail = payload.detail;
      } else if (payload.detail && typeof payload.detail === "object") {
        // FastAPI може повертати detail як object {code, message}
        code = payload.detail.code ?? null;
        detail = payload.detail.message ?? detail;
      }
      if (payload.maintenance === true) isMaint = true;
    } catch {
      /* keep statusText */
    }
    if (res.status === 503 && isMaint) {
      setMaintenance(true, detail);
    }
    if (res.status === 403 && code === "onboarding_required") {
      setOnboardingRequired(true);
    }
    throw new ApiError(
      res.status,
      detail,
      isMaint,
      code,
      res.status === 429 ? res.headers.get("Retry-After") : null,
    );
  }

  // Успішна відповідь — скидаємо onboarding flag (юзер пройшов gate і
  // тепер API повертає 200 → пускаємо в Mini App).
  if (_onboardingRequired) setOnboardingRequired(false);

  // Успішна відповідь — якщо ми були у maintenance, скидаємо
  if (_maintenance) setMaintenance(false, "");

  // 204 No Content — возвращаем undefined приведённое к T
  if (res.status === 204) return undefined as T;

  return (await res.json()) as T;
}
